/**
 * Plain text or HTML clipboard from Sheets/Excel, plain often has only the first cell.
 * @param {DataTransfer | null} clipboardData
 * @returns {string}
 */
export function clipboardHeaderPasteText(clipboardData) {
  if (!clipboardData) return '';

  const html = clipboardData.getData('text/html') || '';
  const fromHtml = parseHeadersFromClipboardHtml(html);
  if (fromHtml.length > 1) return fromHtml.join('\t');
  if (fromHtml.length === 1 && fromHtml[0].includes('\t')) return fromHtml[0];

  const plain = clipboardData.getData('text/plain') || clipboardData.getData('text') || '';
  if (plainLooksLikeHeaderRow(plain)) return plain;

  return plain;
}

/**
 * How many columns the clipboard HTML row appears to have (0 if unknown).
 * @param {DataTransfer | null} clipboardData
 */
export function clipboardHeaderColumnCount(clipboardData) {
  if (!clipboardData) return 0;
  const html = clipboardData.getData('text/html') || '';
  return parseHeadersFromClipboardHtml(html).length;
}

/**
 * @param {string} plain
 */
function plainLooksLikeHeaderRow(plain) {
  if (!plain) return false;
  if (/\t/.test(plain)) return true;
  const lines = plain.replace(/\r\n/g, '\n').split('\n').map((l) => l.trim()).filter(Boolean);
  if (lines.length > 1) return true;
  if (lines.length === 1 && lines[0].includes(',')) {
    return splitCsvLine(lines[0]).length > 1;
  }
  return false;
}

/**
 * @param {string} html
 * @returns {string[]}
 */
export function parseHeadersFromClipboardHtml(html) {
  if (!html) return [];

  const fromTable = parseHtmlTableRows(html);
  if (fromTable.length > 1) return fromTable;

  if (!/<table|google-sheets-html-origin|<td|<th/i.test(html)) {
    const text = html.replace(/<[^>]+>/g, '\t').replace(/\t+/g, '\t').trim();
    if (/\t/.test(text)) return splitCells(text, '\t');
    return fromTable;
  }

  const fromRegex = parseHtmlRowCellsRegex(html);
  if (fromRegex.length > fromTable.length) return fromRegex;

  return fromTable;
}

/**
 * Pick the table row with the most direct cells (handles Sheets/Excel wrapper markup).
 * @param {string} html
 * @returns {string[]}
 */
function parseHtmlTableRows(html) {
  if (!html) return [];

  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const rows = doc.querySelectorAll('table tr, tr');
    let best = [];

    rows.forEach((row) => {
      const cells = Array.from(row.querySelectorAll(':scope > td, :scope > th')).map(normalizeCellText);
      const trimmed = trimTrailingEmpty(cells);
      if (trimmed.length > best.length) best = trimmed;
    });

    return best;
  } catch {
    return [];
  }
}

/**
 * Fallback when DOMParser misses cells (fragment HTML, odd namespaces).
 * @param {string} html
 * @returns {string[]}
 */
function parseHtmlRowCellsRegex(html) {
  if (!html || !/<t[dh]/i.test(html)) return [];

  const rowMatches = html.match(/<tr[\s\S]*?<\/tr>/gi) || [];
  let best = [];

  rowMatches.forEach((rowHtml) => {
    const cells = [];
    const cellRe = /<t[dh][^>]*>([\s\S]*?)<\/t[dh]>/gi;
    let match = cellRe.exec(rowHtml);
    while (match) {
      cells.push(stripHtmlToText(match[1]));
      match = cellRe.exec(rowHtml);
    }
    const trimmed = trimTrailingEmpty(cells);
    if (trimmed.length > best.length) best = trimmed;
  });

  return best;
}

/**
 * @param {string} html
 */
function stripHtmlToText(html) {
  return html
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<[^>]+>/g, '')
    .replace(/\u00a0/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * @param {Element} el
 */
function normalizeCellText(el) {
  return el.textContent.replace(/\u00a0/g, ' ').replace(/\s+/g, ' ').trim();
}

/**
 * @param {string} text
 * @param {string[]} headers
 */
export function isStructuredHeaderPaste(text, headers) {
  if (!headers?.length) return false;
  if (/\t/.test(text)) return true;
  if (headers.length > 1) return true;
  if (/\n/.test(text)) return true;
  if (text.includes(',') && headers.length > 1) return true;
  return false;
}

/**
 * Parse pasted headers from Google Sheets.
 * - Row copy (usual): tab-separated on one line → A, B, C…
 * - Column copy: one header per line → still mapped to A, B, C…
 * @param {string} text
 * @returns {string[]}
 */
export function parseHeaderRow(text) {
  const raw = String(text || '')
    .replace(/^\uFEFF/, '')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .trim();
  if (!raw) return [];

  const lines = raw.split('\n').map((line) => line.trim()).filter(Boolean);
  if (!lines.length) return [];

  const firstLine = lines[0];

  if (firstLine.includes('\t')) {
    return trimTrailingEmpty(splitCells(firstLine, '\t'));
  }

  if (lines.length === 1 && firstLine.includes(',')) {
    return trimTrailingEmpty(splitCsvLine(firstLine));
  }

  if (lines.length > 1 && !raw.includes('\t')) {
    return lines;
  }

  return trimTrailingEmpty([firstLine]);
}

/**
 * @param {string[]} cells
 */
function trimTrailingEmpty(cells) {
  const result = [...cells];
  while (result.length > 0 && result[result.length - 1] === '') {
    result.pop();
  }
  return result;
}

/**
 * @param {string} line
 * @param {string} sep
 */
function splitCells(line, sep) {
  return line.split(sep).map((cell) => cell.trim().replace(/^"|"$/g, ''));
}

/**
 * @param {string} line
 */
function splitCsvLine(line) {
  const cells = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === ',' && !inQuotes) {
      cells.push(current.trim());
      current = '';
    } else {
      current += ch;
    }
  }
  cells.push(current.trim());
  return cells.map((c) => c.replace(/^"|"$/g, ''));
}
